import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography,  IconButton, useMediaQuery, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useDashboard } from '../../../context/DashboardContext';
import { isOnlyHospitalTabAccess } from '../../../utilis/common';
import { BlueColorBox, GreenColorBox, LightGreenColorBox, OrangeColorBox, RedColorBox, RedefinePercentagesPopup, YellowColorBox } from './ColorInfoBox';
import { setPercentageThresholds } from '../../../store/reducers/comparisonReducers/comparison.slice';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

const MobileColorInfoBox = ({
    style,
    loading,
    isLayoutMode,
    isMobile = true,
    comparingAgainst,
    comparingAgainstScaled,
    numberOfDays
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const {
        rangesSet,
        comparisonRanges,
        activeComparisonFacilitiesId,
        facilityByAccount,
        comparingAgainstDifferentFacility
    } = useSelector((state) => state.comparison);

    const { dashboardData: { dbData = null, dbDataComparison = null } } = useDashboard();

    const isOnlyHospitalDashboard = useMemo(() => {
        return isOnlyHospitalTabAccess();
    }, []);

    const matchingNames = useMemo(() => {
        const facilityNames = facilityByAccount
            .filter(({ _id }) => activeComparisonFacilitiesId.includes(_id))
            .map(({ name }) => name)
            .join(", ");

        const label = activeComparisonFacilitiesId.length === 1 ? "Facility Compared To" : "Facilities Compared To";
        return { label, facilityNames };
    }, [facilityByAccount, activeComparisonFacilitiesId]);

    const comparingAgainstScaledByCensus = useMemo(() => {
        if (
            comparingAgainstScaled &&
            dbDataComparison &&
            !_.isEmpty(dbDataComparison) &&
            comparingAgainstDifferentFacility &&
            dbData?.censusAverage
        ) {
            const currentCensus = dbDataComparison.censusAverage / dbData.censusAverage;
            return Math.round(comparingAgainstScaled / currentCensus) || 0;
        }
        return comparingAgainstScaled;
    }, [comparingAgainstScaled, dbDataComparison, dbData, comparingAgainstDifferentFacility]);

    const DateDifference = ({ comparisonRanges }) => {
        if (comparisonRanges && comparisonRanges.length > 0) {
            const { startDate, endDate } = comparisonRanges[0];

            if (startDate && endDate) {
                const formattedStartDate = moment(startDate).format("MM/DD/YYYY");
                const formattedEndDate = moment(endDate).format("MM/DD/YYYY");
                const daysDifference = moment(endDate).diff(moment(startDate), "days");

                return (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        {`${formattedStartDate} - ${formattedEndDate} (${daysDifference} days)`}
                    </Typography>
                );
            }
        }
        return null;
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const percentageThresholds = useSelector((state) => state.comparison.percentageThresholds);
    const [popupOpened, setPopupOpened] = useState(false);
    const { isExpandedSideBar } = useSelector((state) => state.common);
    const isMd = useMediaQuery("(max-width:1281px)");

    const anchorRef = useRef(null);
    const dispatch = useDispatch();


    const closePopup = useCallback(() => {
        setPopupOpened(false);
    }, []);

    const setShowColor = (event, label) => {
        const checked = Number(event.target.checked);
        !isNaN(checked) &&
            dispatch(
                setPercentageThresholds({
                    ...percentageThresholds,
                    [label]: { ...percentageThresholds[label], showColor: checked },
                })
            );
    };


    return (
        <Box sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            position: 'relative'
        }}>
            {/* Left Column - Checkbox and Color Box */}
            <Box sx={{
                width: isExpanded ? '100%' : '25%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: isExpanded ? 'none' : '1px solid #e0e0e0',
                position: 'relative',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                gap: 1,
                transform: isExpanded ? 'translateX(0)' : 'translateX(0)',
                ...!isExpanded && {
                    marginTop: '13px'
                }
            }}>
                {/* Checkbox and Color Box in one row */}
                {
                    isExpanded ? (
                        <Grid
                            container
                            direction="column"
                        >
                            <Typography
                                onClick={() => setPopupOpened((oldState) => !oldState)}
                                ref={anchorRef}
                                sx={{
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    marginBottom: '10px'
                                }}
                            >
                                Redefine percentages
                            </Typography>
                            <RedefinePercentagesPopup
                                open={popupOpened}
                                anchorRef={anchorRef}
                                handleClose={closePopup}
                                percentageThresholds={percentageThresholds}
                            />
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <RedColorBox
                                    isMobile={isMobile}
                                    percentageThresholds={percentageThresholds}
                                    setShowColor={setShowColor}
                                    isFullWidth={true}
                                />
                            </Grid>                           
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <OrangeColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isFullWidth={true} />
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <YellowColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isFullWidth={true} />
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <LightGreenColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isFullWidth={true} />
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <GreenColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isFullWidth={true} />
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    '@media (max-width:1280px)': { height: '32px' },
                                    ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                    ...(isMobile && {
                                        height: '24px',
                                        minHeight: '24px'
                                    })
                                }}
                            >
                                <BlueColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isFullWidth={true} />
                            </Grid>
                        </Grid>
                    )
                        : (
                            <Grid
                                container
                                direction="column"
                                spacing={{ mt:2,lg: 1 }}
                            >
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <RedColorBox isMobile={isMobile} percentageThresholds={percentageThresholds} setShowColor={setShowColor} isOnlyBox={true} />
                                </Grid>
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <OrangeColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isOnlyBox={true} />
                                </Grid>
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <YellowColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isOnlyBox={true} />
                                </Grid>
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <LightGreenColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isOnlyBox={true} />
                                </Grid>
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <GreenColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isOnlyBox={true} />
                                </Grid>
                                <Grid
                                    container
                                    sx={{
                                        '@media (max-width:1280px)': { height: '32px' },
                                        ...(isMd && isExpandedSideBar && { justifyContent: "center" }),
                                        ...(isMobile && {
                                            height: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                >
                                    <BlueColorBox percentageThresholds={percentageThresholds} setShowColor={setShowColor} isMobile={isMobile} isOnlyBox={true} />
                                </Grid>
                            </Grid>
                        )}
            </Box>

            {/* Expand/Collapse Arrow - Positioned relative to parent container */}
            <IconButton
                onClick={toggleExpanded}
                size="small"
                sx={{
                    position: 'absolute',
                    left: isExpanded ? 'calc(100% - 24px)' : 'calc(25% - 12px)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    zIndex: 2,
                    width: '24px',
                    height: '24px',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                        backgroundColor: '#f5f5f5'
                    }
                }}
            >
                {isExpanded ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>

            {/* Right Column - Comparison Information with smooth slide animation */}
            <Box sx={{
                width: isExpanded ? '0%' : '75%',
                pl: 2,
                pt: 1,
                overflow: 'hidden',
                opacity: isExpanded ? 0 : 1,
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transform: isExpanded ? 'translateX(100px)' : 'translateX(0)',
                pointerEvents: isExpanded ? 'none' : 'auto',
                display: isExpanded ? 'none' : 'block'
            }}>
                {/* Facility Names */}
                {matchingNames && comparingAgainstDifferentFacility && rangesSet && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            {matchingNames.label}:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {matchingNames.facilityNames}
                        </Typography>
                    </Box>
                )}

                {/* Comparison Date */}
                <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        Comparison Date:
                    </Typography>
                    <DateDifference comparisonRanges={comparisonRanges} />
                </Box>

                {/* Transfers Count */}
                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ fontSize: '0.85rem' }}>
                        {comparingAgainst || 0} Transfers
                    </Typography>
                </Box>

                {/* Projection Details */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        {dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility ? (
                            isOnlyHospitalDashboard ? (
                                <>
                                    {numberOfDays || 0} Day Projection / Bed Capacity - {dbData?.bedCapacity || 0}:
                                </>
                            ) : (
                                <>
                                    {numberOfDays || 0} Day Projection / Avg. Census - {dbData?.censusAverage || 0}:
                                </>
                            )
                        ) : (
                            <>
                                {`${numberOfDays || 0} Day Projection:`}
                            </>
                        )}
                    </Typography>

                    {!(dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility) && (
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ fontSize: '0.85rem' }}>
                            {comparingAgainstScaled || 0} Transfers
                        </Typography>
                    )}
                </Box>

                {/* Census Adjusted Transfers */}
                {dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility && (
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ fontSize: '0.85rem' }}>
                            {comparingAgainstScaledByCensus || 0} Transfers
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MobileColorInfoBox; 